import { create } from 'zustand';
import type { CatalogProductRow } from '../domain/catalogTypes';
import type { CatalogItemTypeFilter } from '../domain/productTypes';
import { emptyDraft, type ProductDraft } from '../domain/productDraft';
import type { ProductLookupResult } from '../domain/barcodeLookup';

type RemoteHit = Extract<ProductLookupResult, { kind: 'remote_hit' }>;
export type CatalogProductFormMode = 'create' | 'edit' | 'duplicate';
export type CatalogProductDetailTab = 'resumen' | 'composicion' | 'receta';
export type CatalogHubSegment = 'products' | 'options' | 'combos';

type CatalogOverlayFields = {
  modalVisible: boolean;
  saving: boolean;
  editingProduct: CatalogProductRow | null;
  formMode: CatalogProductFormMode;
  draft: ProductDraft;
  searchScannerVisible: boolean;
  formScannerVisible: boolean;
  filtersExpanded: boolean;
  lookupFeedback: string | null;
  remoteSuggestion: RemoteHit | null;
  formSuggestion: RemoteHit | null;
  selectedProduct: CatalogProductRow | null;
  priceMarginPercentInput: string;
  /** Intent from Agenda / other modules: open create-service form on Catalog. */
  pendingOpenNewService: boolean;
  /** Intent from Inicio → «Cargar producto»: open create-product form on Catalog. */
  pendingOpenNewProduct: boolean;
  /** Intent from Compras / otros módulos: abrir el hub de maestros en una sección. */
  pendingOpenMastersSection: 'categories' | 'brands' | 'suppliers' | null;
  /** Formulario reducido (nombre, precio, categoría opcional). */
  formCompact: boolean;
  /** Pestaña activa en la ficha de producto (Resumen / Composición / Receta). */
  selectedProductDetailTab: CatalogProductDetailTab;
  /** Sub-modo dentro de tab Receta. */
  selectedProductRecipeKind: 'assemble' | 'disassemble';
  /** Deep-link: abrir ficha de producto (desde hub Combos u otros). */
  pendingOpenProductDetail: {
    productId: string;
    tab: CatalogProductDetailTab;
    recipeKind?: 'assemble' | 'disassemble';
  } | null;
  /** Cambiar segmento del hub Catálogo (p. ej. Combos → Productos). */
  pendingCatalogHubSegment: CatalogHubSegment | null;
  /** Aplicar filtro de tipo al montar Catálogo → Productos (ej. desde hub Combos). */
  pendingCatalogItemTypeFilter: CatalogItemTypeFilter | null;
  /** Abrir form en rail Composición / Receta (desde ficha o deep-link). */
  pendingFormRailSection: 'composition' | 'receta' | null;
};

type CatalogOverlayState = CatalogOverlayFields & {
  setModalVisible: (v: boolean) => void;
  setSaving: (v: boolean) => void;
  setEditingProduct: (p: CatalogProductRow | null) => void;
  setFormMode: (m: CatalogProductFormMode) => void;
  setDraft: (d: ProductDraft | ((prev: ProductDraft) => ProductDraft)) => void;
  setSearchScannerVisible: (v: boolean) => void;
  setFormScannerVisible: (v: boolean) => void;
  setFiltersExpanded: (v: boolean) => void;
  setLookupFeedback: (s: string | null) => void;
  setRemoteSuggestion: (r: RemoteHit | null) => void;
  setFormSuggestion: (r: RemoteHit | null) => void;
  setSelectedProduct: (p: CatalogProductRow | null) => void;
  setPriceMarginPercentInput: (s: string) => void;
  setFormCompact: (v: boolean) => void;
  requestOpenNewService: () => void;
  consumeOpenNewServiceRequest: () => boolean;
  requestOpenNewProduct: () => void;
  consumeOpenNewProductRequest: () => boolean;
  requestOpenMasters: (section: 'categories' | 'brands' | 'suppliers') => void;
  consumeOpenMastersRequest: () => 'categories' | 'brands' | 'suppliers' | null;
  setSelectedProductDetailTab: (tab: CatalogProductDetailTab) => void;
  setSelectedProductRecipeKind: (kind: 'assemble' | 'disassemble') => void;
  requestOpenProductDetail: (
    productId: string,
    tab?: CatalogProductDetailTab,
    recipeKind?: 'assemble' | 'disassemble',
  ) => void;
  consumeOpenProductDetailRequest: () => {
    productId: string;
    tab: CatalogProductDetailTab;
    recipeKind?: 'assemble' | 'disassemble';
  } | null;
  requestCatalogHubSegment: (segment: CatalogHubSegment) => void;
  consumeCatalogHubSegmentRequest: () => CatalogHubSegment | null;
  requestCatalogItemTypeFilter: (filter: CatalogItemTypeFilter) => void;
  consumeCatalogItemTypeFilterRequest: () => CatalogItemTypeFilter | null;
  requestFormRailSection: (section: 'composition' | 'receta') => void;
  consumeFormRailSectionRequest: () => 'composition' | 'receta' | null;
  resetAll: () => void;
};

const catalogOverlayDefaults = (): CatalogOverlayFields => ({
  modalVisible: false,
  saving: false,
  editingProduct: null,
  formMode: 'create',
  draft: { ...emptyDraft },
  searchScannerVisible: false,
  formScannerVisible: false,
  filtersExpanded: false,
  lookupFeedback: null,
  remoteSuggestion: null,
  formSuggestion: null,
  selectedProduct: null,
  priceMarginPercentInput: '35',
  pendingOpenNewService: false,
  pendingOpenNewProduct: false,
  pendingOpenMastersSection: null,
  formCompact: false,
  selectedProductDetailTab: 'resumen',
  selectedProductRecipeKind: 'assemble',
  pendingOpenProductDetail: null,
  pendingCatalogHubSegment: null,
  pendingCatalogItemTypeFilter: null,
  pendingFormRailSection: null,
});

export const useCatalogOverlayStore = create<CatalogOverlayState>((set, get) => ({
  ...catalogOverlayDefaults(),
  setModalVisible: (v) => set({ modalVisible: v }),
  setSaving: (v) => set({ saving: v }),
  setEditingProduct: (p) => set({ editingProduct: p }),
  setFormMode: (m) => set({ formMode: m }),
  setDraft: (d) => set((state) => ({ draft: typeof d === 'function' ? d(state.draft) : d })),
  setSearchScannerVisible: (v) => set({ searchScannerVisible: v }),
  setFormScannerVisible: (v) => set({ formScannerVisible: v }),
  setFiltersExpanded: (v) => set({ filtersExpanded: v }),
  setLookupFeedback: (s) => set({ lookupFeedback: s }),
  setRemoteSuggestion: (r) => set({ remoteSuggestion: r }),
  setFormSuggestion: (r) => set({ formSuggestion: r }),
  setSelectedProduct: (p) => set({ selectedProduct: p }),
  setPriceMarginPercentInput: (s) => set({ priceMarginPercentInput: s }),
  setFormCompact: (v) => set({ formCompact: v }),
  requestOpenNewService: () => set({ pendingOpenNewService: true }),
  consumeOpenNewServiceRequest: () => {
    if (!get().pendingOpenNewService) return false;
    set({ pendingOpenNewService: false });
    return true;
  },
  requestOpenNewProduct: () => set({ pendingOpenNewProduct: true }),
  consumeOpenNewProductRequest: () => {
    if (!get().pendingOpenNewProduct) return false;
    set({ pendingOpenNewProduct: false });
    return true;
  },
  requestOpenMasters: (section) => set({ pendingOpenMastersSection: section }),
  consumeOpenMastersRequest: () => {
    const section = get().pendingOpenMastersSection;
    if (!section) return null;
    set({ pendingOpenMastersSection: null });
    return section;
  },
  setSelectedProductDetailTab: (tab) => set({ selectedProductDetailTab: tab }),
  setSelectedProductRecipeKind: (kind) => set({ selectedProductRecipeKind: kind }),
  requestOpenProductDetail: (productId, tab = 'composicion', recipeKind) =>
    set({
      pendingOpenProductDetail: {
        productId,
        tab,
        ...(recipeKind ? { recipeKind } : {}),
      },
      ...(recipeKind ? { selectedProductRecipeKind: recipeKind } : {}),
      ...(tab ? { selectedProductDetailTab: tab } : {}),
    }),
  consumeOpenProductDetailRequest: () => {
    const pending = get().pendingOpenProductDetail;
    if (!pending) return null;
    set({ pendingOpenProductDetail: null });
    return pending;
  },
  requestCatalogHubSegment: (segment) => set({ pendingCatalogHubSegment: segment }),
  consumeCatalogHubSegmentRequest: () => {
    const segment = get().pendingCatalogHubSegment;
    if (!segment) return null;
    set({ pendingCatalogHubSegment: null });
    return segment;
  },
  requestCatalogItemTypeFilter: (filter) =>
    set({ pendingCatalogItemTypeFilter: filter }),
  consumeCatalogItemTypeFilterRequest: () => {
    const filter = get().pendingCatalogItemTypeFilter;
    if (!filter) return null;
    set({ pendingCatalogItemTypeFilter: null });
    return filter;
  },
  requestFormRailSection: (section) => set({ pendingFormRailSection: section }),
  consumeFormRailSectionRequest: () => {
    const section = get().pendingFormRailSection;
    if (!section) return null;
    set({ pendingFormRailSection: null });
    return section;
  },
  resetAll: () => set(catalogOverlayDefaults()),
}));

/** Cierra detalle, formularios y modales al salir del tab Catálogo (mantiene filtros de lista). */
export function resetCatalogTabOverlays() {
  useCatalogOverlayStore.setState({
    selectedProduct: null,
    modalVisible: false,
    saving: false,
    editingProduct: null,
    formMode: 'create',
    draft: { ...emptyDraft },
    searchScannerVisible: false,
    formScannerVisible: false,
    lookupFeedback: null,
    remoteSuggestion: null,
    formSuggestion: null,
    pendingOpenNewService: false,
    pendingOpenNewProduct: false,
    pendingOpenMastersSection: null,
    formCompact: false,
    selectedProductDetailTab: 'resumen',
    selectedProductRecipeKind: 'assemble',
    pendingOpenProductDetail: null,
    pendingCatalogHubSegment: null,
    pendingCatalogItemTypeFilter: null,
    pendingFormRailSection: null,
  });
}
